import { Component, OnChanges, SimpleChanges, ChangeDetectionStrategy, input, output } from '@angular/core';
import {WorkingSureFlowUnit} from '../../model/working-sure-flow-unit';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {SelectionModel} from '@angular/cdk/collections';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {DecimalPipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatTooltip} from '@angular/material/tooltip';
import {MatCheckbox} from '@angular/material/checkbox';

@Component({
    selector: 'app-sure-flow-units-grid',
    templateUrl: './sure-flow-units-grid.component.html',
    styleUrls: ['./sure-flow-units-grid.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCheckbox, MatCellDef, MatCell, MatTooltip, FormsModule, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, DecimalPipe]
})
export class SureFlowUnitsGridComponent implements OnChanges {

  readonly isCooling = input.required<boolean>();
  readonly isMetric = input.required<boolean>();
  readonly units = input.required<WorkingSureFlowUnit[]>();
  readonly invalidUnitIds = input.required<number[]>();
  readonly selectedUnitIds = input.required<number[]>();
  readonly unitSelectionChanged = output<WorkingSureFlowUnit[]>();
  readonly datasource: MatTableDataSource<WorkingSureFlowUnit> = new MatTableDataSource<WorkingSureFlowUnit>();
  selection: SelectionModel<WorkingSureFlowUnit> = new SelectionModel<WorkingSureFlowUnit>(true, []);
  displayColumns!: string[];

  ngOnChanges(changes: SimpleChanges) {
    this.datasource.data = this.units();
    if (this.isCooling()) {
      this.displayColumns = ['select', 'userTag', 'coolingTargetTotalLoad', 'modelSize', 'coilRows', 'airflow', 'edb', 'ewb', 'eft', 'totalCap', 'sensibleCap', 'latentCap', 'active'];
    } else {
      this.displayColumns = ['select', 'userTag', 'heatingTargetTotalLoad', 'modelSize', 'coilRows', 'airflow', 'edb', 'eft', 'totalCap', 'active'];
    }
  }

  getFluidFlowRate(unit: WorkingSureFlowUnit): number {
    if (this.isCooling()) {
      return unit?.unitData?.sureFlowRatingUnit?.response?.primaryCoilResponse?.fluidFlowRate || unit.unitData.coolingFluidFlowRate;
    } else {
      return unit?.unitData?.sureFlowRatingUnit?.response?.secondaryCoilResponse?.fluidFlowRate || unit?.unitData?.sureFlowRatingUnit?.response?.primaryCoilResponse?.fluidFlowRate || unit.unitData.heatingFluidFlowRate;
    }
  }

  getEnteringDryBulb(unit: WorkingSureFlowUnit): number {
    if (this.isCooling()) {
      return unit?.unitData?.sureFlowRatingUnit?.request?.primaryCoilRequest?.enteringDryBulb || undefined!;
    } else {
      return unit?.unitData?.sureFlowRatingUnit?.request?.secondaryCoilRequest?.enteringDryBulb || unit?.unitData?.sureFlowRatingUnit?.request?.primaryCoilRequest?.enteringDryBulb || undefined!;
    }
  }

  getEnteringWetBulb(unit: WorkingSureFlowUnit): number {
    if (this.isCooling()) {
      return unit?.unitData?.sureFlowRatingUnit?.request?.primaryCoilRequest?.enteringWetBulb || undefined!;
    }
    return 0;
  }

  isInvalidUnit(unit: WorkingSureFlowUnit): boolean {
    if ((this.invalidUnitIds()?.length || 0) > 0) {
      return this.invalidUnitIds().indexOf(unit.id) >= 0;
    }
    return false;
  }

  getEnteringFluidTemperature(unit: WorkingSureFlowUnit): number {
    if (this.isCooling()) {
      return unit?.unitData?.coolingEnteringFluidTemperature || undefined!;
    } else {
      return unit?.unitData?.heatingEnteringFluidTemperature || undefined!;
    }
  }

  getTotalCapacity(unit: WorkingSureFlowUnit): number {
    if (this.isCooling()) {
      return unit?.unitData?.sureFlowRatingUnit?.response?.primaryCoilResponse?.totalCapacity || undefined!;
    } else {
      return unit?.unitData?.sureFlowRatingUnit?.response?.secondaryCoilResponse?.totalCapacity || unit?.unitData?.sureFlowRatingUnit?.response?.primaryCoilResponse?.totalCapacity || undefined!;
    }
  }

  getSensibleCapacity(unit: WorkingSureFlowUnit): number {
    if (this.isCooling()) {
      return unit?.unitData?.sureFlowRatingUnit?.response?.primaryCoilResponse?.sensibleCapacity || undefined!;
    }
    return undefined!;
  }

  getLatentCapacity(unit: WorkingSureFlowUnit): number {
    const totalCapacity = this.getTotalCapacity(unit);
    if (totalCapacity) {
      return totalCapacity - this.getSensibleCapacity(unit);
    }
    return undefined!;
  }

  unitSelected(unit: WorkingSureFlowUnit): void {
    const selectedUnits: WorkingSureFlowUnit[] = this.units().filter(unit => this.isSelected(unit));
    if (this.isSelected(unit)) {
      const selectedUnit = selectedUnits.filter(selection => selection.id === unit.id)[0];
      const index = selectedUnits.indexOf(selectedUnit);
      selectedUnits.splice(index, 1);
    } else {
      selectedUnits.push(unit);
    }
    this.unitSelectionChanged.emit(selectedUnits);
  }

  isSelected(unit: WorkingSureFlowUnit): boolean {
    return this.selectedUnitIds()?.indexOf(unit.id) >= 0;
  }

  isAllSelected(): boolean {
    const unitCount = this.units()?.length || 0;
    const selectedCount = this.selectedUnitIds()?.length || 0;
    return unitCount > 0 && unitCount === selectedCount;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.unitSelectionChanged.emit([]);
    } else {
      this.unitSelectionChanged.emit(this.units());
    }
  }

  toggleActiveFlags(): void {
    if ((this.units()?.length || 0) > 0) {
      const isActive: boolean = !this.areAllActive();
      for (const unit of this.units()) {
        unit.unitData.active = isActive;
      }
    }
  }

  private areAllActive(): boolean {
    const inactive = this.units()?.filter(unit => !unit.unitData.active)?.length || 0;
    return (this.units()?.length || 0) > 0 && inactive === 0;
  }

  getCapacityUnits(): string {
    return UnitConversionUtils.getCapacityUnits(this.isMetric());
  }

  getFlowRateUnits(): string {
    return UnitConversionUtils.getFlowRateUnits(this.isMetric());
  }

  getStaticPressureUnits(): string {
    return UnitConversionUtils.getEspUnits(this.isMetric());
  }

  getAirflowUnits(): string {
    return UnitConversionUtils.getAirflowUnits(this.isMetric());
  }

  getTemperatureUnits(): string {
    return UnitConversionUtils.getTemperatureUnits(this.isMetric());
  }

}
